import React, { useState, useRef } from 'react';
import { useTheme } from '../../styles/ThemeProvider';
import { useCockpitData } from '../../hooks/useCockpitData';
import { useFirmalar } from '../../hooks/useFirmalar';
import {
  FilterBar, DateRangePicker, ExportButton, KpiCard, ChartCard,
  DataTable, TrendLine, ComparisonBar, CategoryBar, Waterfall,
} from '../../components/analytics';
import PnlCompactTable from '../../components/yonetim/PnlCompactTable';
import ForecastBand from '../../components/yonetim/ForecastBand';
import BilgiKartlari from '../../components/yonetim/BilgiKartlari';
import WhatifPanel from '../../components/yonetim/WhatifPanel';
import { formatTRY, formatCompact, formatPct } from '../../utils/analytics/format';
import { exportPdf } from '../../utils/analytics/export';
import { getPresetRange, toIsoDate } from '../../utils/analytics/dateRange';

const initialRange = () => {
  const r = getPresetRange('thisMonth');
  return { preset: 'thisMonth', start: toIsoDate(r.start), end: toIsoDate(r.end) };
};

const KPI_DRILL_DOWN = {
  net_ciro: '/analiz/satis',
  brut_kar: '/analiz/pnl',
  net_kar: '/analiz/pnl',
  ebitda: '/analiz/pnl',
  komisyon_yuku: '/analiz/komisyon-detay',
  prime_cost: '/analiz/pnl',
  kritik_uyari: '/analiz/stok-uretim',
};

// Tahakkuk tahmini kaynak etiketi — backend "+".join'li çoklu token döndürebilir
// (örn. "taslak+kapanmis_bordro"); her token okunur Türkçeye çevrilir.
const TAHAKKUK_KAYNAK_AD = {
  taslak: 'taslak puantaj',
  runrate: 'son ay temposu',
  kapanmis_bordro: 'kapanmış bordro',
};
const tahakkukKaynakEtiket = (kaynak) =>
  String(kaynak || '')
    .split('+')
    .map((t) => TAHAKKUK_KAYNAK_AD[t] || t)
    .filter(Boolean)
    .join(' + ') || 'tahmin';

// KPI value formatlamak için: prime_cost % değer, kritik_uyari sayı, diğerleri TL
const formatKpiValue = (k) => {
  if (k.value === null || k.value === undefined) return null;
  if (k.key === 'kritik_uyari') return String(k.value);
  if (k.key === 'prime_cost') return formatPct(k.value);
  return formatTRY(k.value);
};

export default function ExecutiveCockpit() {
  const { theme } = useTheme();
  const [range, setRange] = useState(initialRange);
  const [firma, setFirma] = useState('');
  const containerRef = useRef(null);

  const { data: firmalar } = useFirmalar();
  const { data: cockpit, loading, error, refresh } = useCockpitData({
    baslangic: range.start, bitis: range.end, firma: firma || undefined,
  });

  const onPdf = () => {
    if (containerRef.current) exportPdf(containerRef.current, `yonetim-paneli-${range.start}_${range.end}`);
  };

  // Drill-down'a cockpit'in dönem + firma bağlamını taşı → tıklanan analiz sayfası
  // aynı filtreyle açılsın (eskiden çıplak path'e gidip kendi default'una düşüyordu).
  const withCtx = (path) => {
    if (!path) return path;
    const p = new URLSearchParams();
    if (range.start) p.set('baslangic', range.start);
    if (range.end) p.set('bitis', range.end);
    if (firma) p.set('firma', firma);
    const q = p.toString();
    if (!q) return path;
    return path + (path.includes('?') ? '&' : '?') + q;
  };
  const drill = (path) => window.location.assign(withCtx(path));

  // firma kod → ad (firma kırılımı etiketleri); bulunamazsa kodu göster
  const firmaAd = (kod) => (firmalar || []).find(f => f.kod === kod)?.ad || kod;

  return (
    <div ref={containerRef} style={{ padding: theme.space.lg, display: 'flex', flexDirection: 'column', gap: theme.space.lg, background: theme.color.surfaceAlt, minHeight: '100vh' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ margin: 0, color: theme.color.text, fontSize: theme.font.size.xxl, fontWeight: theme.font.weight.bold }}>Karar Destek Paneli</h1>
          <div style={{ color: theme.color.textSoft, fontSize: theme.font.size.sm, marginTop: 4 }}>
            {cockpit?.generated_at && `Son güncelleme: ${new Date(cockpit.generated_at).toLocaleString('tr-TR')}`}
          </div>
        </div>
      </header>

      <FilterBar>
        <DateRangePicker value={range} onChange={setRange} />
        <select
          value={firma}
          onChange={(e) => setFirma(e.target.value)}
          style={{
            padding: `${theme.space.xs}px ${theme.space.md}px`,
            borderRadius: theme.radius.sm,
            border: `1px solid ${theme.color.border}`,
            background: theme.color.surface,
            color: theme.color.text,
            fontSize: theme.font.size.xs,
            fontFamily: theme.font.family,
          }}>
          <option value="">Tüm Firmalar</option>
          {(firmalar || []).map(f => <option key={f.kod} value={f.kod}>{f.ad}</option>)}
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: theme.space.sm }}>
          <button onClick={refresh} style={{
            padding: `${theme.space.xs}px ${theme.space.md}px`,
            borderRadius: theme.radius.sm,
            border: `1px solid ${theme.color.border}`,
            background: theme.color.surface,
            color: theme.color.text,
            fontSize: theme.font.size.xs,
            cursor: 'pointer',
            fontFamily: theme.font.family,
          }}>↻ Yenile</button>
          <ExportButton onExportPdf={onPdf} />
        </div>
      </FilterBar>

      {/* Bilgi kartları (alarm sistemi) — eşik bazlı, sebep/çözüm yok */}
      <BilgiKartlari
        kartlar={cockpit?.bilgi_kartlari || []}
        onDrill={(path) => drill(path)}
      />

      {/* Vergi tahakkuku Faz 2: panelde EKSİK kalan tahmini işçilik+vergi
          EBITDA/Net Kâr'dan DÜŞÜLMÜŞ gösterilir — şerit bunu açıkça söyler.
          (Geçmiş ay: bordro kapanmış ama vergi tahakkuku girilmemişse o eksik
          vergi de tahmin edilir — 2026-06-23 fix.) */}
      {cockpit?.tahakkuk_tahmini && (
        <div style={{
          background: '#fff8e1', border: '1px solid #ffe082', borderRadius: theme.radius.sm,
          padding: `${theme.space.sm}px ${theme.space.md}px`, fontSize: theme.font.size.xs,
          color: '#7a5d00', display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span>ℹ️</span>
          <span>
            EBITDA ve Net Kâr, panelde eksik kalan <b>tahmini işçilik+vergi
            ({formatTRY(cockpit.tahakkuk_tahmini.toplam_tl)})</b> düşülmüş değerdir
            (kaynak: {tahakkukKaynakEtiket(cockpit.tahakkuk_tahmini.kaynak)}) — gerçek
            bordro/tahakkuk gideri girilince kesinleşir.
          </span>
        </div>
      )}

      {/* Hesaplanan KDV (kullanıcı talebi 2026-06-23): satış KDV'si net kârdan
          düşülür. KDV-dahil ciroda matrah=ciro/(1+oran) → KDV=ciro×oran/(100+oran). */}
      {(cockpit?.pnl_compact?.hesaplanan_kdv_tl || 0) > 0 && (
        <div style={{
          background: '#e8f4fd', border: '1px solid #b3e0ff', borderRadius: theme.radius.sm,
          padding: `${theme.space.sm}px ${theme.space.md}px`, fontSize: theme.font.size.xs,
          color: '#0a4f7a', display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span>ℹ️</span>
          <span>
            Net Kâr, <b>net KDV ({formatTRY(cockpit.pnl_compact.net_kdv_tl ?? cockpit.pnl_compact.hesaplanan_kdv_tl)})</b> kadar
            düşülmüş değerdir — Hesaplanan {formatTRY(cockpit.pnl_compact.hesaplanan_kdv_tl)} − İndirilecek{' '}
            {formatTRY(cockpit.pnl_compact.indirilecek_kdv_tl || 0)} (%{cockpit.pnl_compact.kdv_orani_pct}{!firma ? ' karma' : ''},
            KDV-dahil ciro üzerinden). EBITDA bu tutardan etkilenmez. Oran Ayarlar'dan değiştirilebilir.
          </span>
        </div>
      )}

      {/* Firma drill-down sınırı: KDV matrahı yalnız POS perakende cirosu;
          firma-bazlı SAS/iç-satış (B2B) çıkış-KDV'si dahil değil → SAS geliri olan
          firmanın (örn. Toruna Kasap) net kârı eksik B2B KDV'si kadar yüksek olabilir.
          Konsolidede SAS=0 (grup-içi netleşir) → not gösterilmez. (Teknik borç.) */}
      {firma && (cockpit?.pnl_compact?.sas_gelir || 0) > 0 && (cockpit?.pnl_compact?.kdv_orani_pct || 0) > 0 && (
        <div style={{
          background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: theme.radius.sm,
          padding: `${theme.space.sm}px ${theme.space.md}px`, fontSize: theme.font.size.xs,
          color: '#8a4b00', display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span>⚠️</span>
          <span>
            Bu firmanın şirketler-arası/B2B satış geliri
            (<b>{formatTRY(cockpit.pnl_compact.sas_gelir)}</b>) var; hesaplanan KDV yalnız
            perakende (POS) cirosu üzerindendir — B2B satış KDV'si dahil değildir, bu
            firmanın net kârı o tutar kadar yüksek görünebilir.
          </span>
        </div>
      )}

      {/* KPI şeridi (8 KPI: net_ciro, brut_kar, net_kar, vergi_sonrasi_net_kar, ebitda, komisyon_yuku, prime_cost, kritik_uyari) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: theme.space.base }}>
        {loading && Array.from({ length: 8 }).map((_, i) => <KpiCard key={i} loading />)}
        {cockpit?.kpis.map(k => (
          <KpiCard
            key={k.key}
            // kritik_uyari firma/dönem'den bağımsız (tüm sistem, anlık) → başlıkta belirt
            title={k.key === 'kritik_uyari' ? `${k.label} (genel)` : k.label}
            value={formatKpiValue(k)}
            delta={k.delta_pct}
            deltaLabel="vs geçen dönem"
            increaseIsBad={k.key === 'kritik_uyari' || k.key === 'komisyon_yuku' || k.key === 'prime_cost'}
            sparklineData={k.sparkline && k.sparkline.length ? k.sparkline : undefined}
            emptyLabel={k.empty_label}
            onClick={KPI_DRILL_DOWN[k.key] ? () => drill(KPI_DRILL_DOWN[k.key]) : undefined}
          />
        ))}
        {/* Şirketler Arası Satış — dönem-içi hacim; kesilmemiş fatura tooltip + alt kart/uyarıda */}
        {cockpit?.sirketler_arasi && (
          <KpiCard
            title="Şirketler Arası Satış"
            value={formatTRY(cockpit.sirketler_arasi.donem_toplam_satis)}
            tooltip={cockpit.sirketler_arasi.kesilmemis_toplam > 0
              ? `${cockpit.sirketler_arasi.kesilmemis_toplam} kesilmemiş Logo faturası (tüm açık)`
              : 'Tüm şirketler arası faturalar kesilmiş'}
            onClick={() => drill('/sirketler-arasi-satislar?tab=OZET')}
          />
        )}
      </div>
      {error && <div style={{ color: theme.color.muted }}>{`Hata: ${error}`}</div>}

      {/* Forecast bandı — ay sonu projeksiyonu */}
      <ForecastBand forecast={cockpit?.forecast} />

      {/* Kompakt P&L Tablosu — komisyonlar görünür satır olarak (must-have) */}
      {/* 2026-05 (faz 2): bucket → kategori → kayıt inline expand desteği */}
      <PnlCompactTable
        data={cockpit?.pnl_compact}
        range={range}
        firma={firma}
        onDrill={(path) => drill(path)}
      />

      {/* Chart grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: theme.space.base }}>
        <ChartCard title="P&L Waterfall" subtitle="Gelir → Giderler → Net Kâr · detay için tıkla" loading={loading}
                   error={error} empty={!loading && (!cockpit?.waterfall || cockpit.waterfall.length === 0)} height={280}>
          {cockpit?.waterfall && (
            <div onClick={() => drill('/analiz/pnl')} style={{ cursor: 'pointer', height: '100%' }} title="Kâr/Zarar analizine git">
              <Waterfall items={cockpit.waterfall} formatter={formatCompact} />
            </div>
          )}
        </ChartCard>

        {/* BG-06: trend tam ay bazlı — seçili dönem filtresinden bağımsız */}
        <ChartCard title="Aylık Ciro Trendi" subtitle="Son 12 ay · tam ay bazlı (dönem filtresinden bağımsız)" loading={loading}
                   error={error} empty={!loading && (!cockpit?.monthly_trend || cockpit.monthly_trend.every(p => p.toplam === 0))} height={280}>
          {cockpit?.monthly_trend && (
            <TrendLine
              data={cockpit.monthly_trend}
              series={[{ key: 'toplam', label: 'Toplam Ciro' }]}
              yFormatter={formatCompact}
            />
          )}
        </ChartCard>

        <ChartCard title="Şube Karşılaştırması" subtitle="Dönem içi ciro/gider/kâr" loading={loading}
                   error={error} empty={!loading && (!cockpit?.branches || cockpit.branches.length === 0)} height={280}>
          {cockpit?.branches && (
            <ComparisonBar
              data={cockpit.branches.map(b => ({ label: b.lokasyon_ad, ciro: b.ciro, gider: b.gider, kar: b.kar }))}
              series={[
                { key: 'ciro', label: 'Ciro' },
                { key: 'gider', label: 'Gider' },
                { key: 'kar', label: 'Kâr' },
              ]}
              yFormatter={formatCompact}
            />
          )}
        </ChartCard>

        <ChartCard title="Top 5 Gider Kategorisi" subtitle="Dönem içi harcama · gider listesine git" loading={loading}
                   error={error} empty={!loading && (!cockpit?.top_expense_categories || cockpit.top_expense_categories.length === 0)} height={280}>
          {cockpit?.top_expense_categories && (
            <div onClick={() => drill('/analiz/pnl')} style={{ cursor: 'pointer', height: '100%' }} title="Gider analizine git">
              <CategoryBar
                data={cockpit.top_expense_categories.map(c => ({ label: c.kategori, value: c.toplam }))}
                formatter={formatCompact}
              />
            </div>
          )}
        </ChartCard>

        {/* Şirketler Arası Satışlar — dönem-içi firma kırılımı + kesilmemiş fatura rozeti; tıkla → Özet Rapor */}
        <ChartCard title="Şirketler Arası Satışlar" subtitle="Dönem içi · firma kırılımı · detay için tıkla" loading={loading}
                   error={error} empty={!loading && (!cockpit?.sirketler_arasi || cockpit.sirketler_arasi.firma_kirilim.length === 0)} height={280}>
          {cockpit?.sirketler_arasi && cockpit.sirketler_arasi.firma_kirilim.length > 0 && (
            <div onClick={() => drill('/sirketler-arasi-satislar?tab=OZET')}
                 style={{ cursor: 'pointer', height: '100%', display: 'flex', flexDirection: 'column' }}
                 title="Şirketler Arası Satışlar Özet Rapor'a git">
              <div style={{ display: 'flex', gap: theme.space.md, marginBottom: theme.space.sm, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: theme.font.size.sm, color: theme.color.textSoft }}>
                  Toplam: <b style={{ color: theme.color.text }}>{formatTRY(cockpit.sirketler_arasi.donem_toplam_satis)}</b>
                </span>
                <span style={{ fontSize: theme.font.size.sm, color: theme.color.textSoft }}>
                  Kâr: <b style={{ color: '#2e7d32' }}>{formatTRY(cockpit.sirketler_arasi.donem_toplam_kar)}</b>
                </span>
                {cockpit.sirketler_arasi.kesilmemis_toplam > 0 && (
                  <span style={{ fontSize: theme.font.size.xs, padding: '2px 8px', borderRadius: 12, background: '#ffebee', color: '#c62828', fontWeight: 600 }}>
                    {cockpit.sirketler_arasi.kesilmemis_toplam} kesilmemiş fatura
                  </span>
                )}
              </div>
              <div style={{ flex: 1, minHeight: 0 }}>
                <CategoryBar
                  data={cockpit.sirketler_arasi.firma_kirilim.map(f => ({ label: firmaAd(f.hedef_firma), value: f.toplam_satis }))}
                  formatter={formatCompact}
                />
              </div>
            </div>
          )}
        </ChartCard>
      </div>

      {/* Alt listeler: Top tedarikçi + Kritik uyarılar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: theme.space.base }}>
        <ChartCard title="Top 5 Tedarikçi" subtitle="Dönem içi hacim" loading={loading} error={error}
                   empty={!loading && (!cockpit?.top_suppliers || cockpit.top_suppliers.length === 0)}>
          {cockpit?.top_suppliers && cockpit.top_suppliers.length > 0 && (
            <DataTable
              columns={[
                { key: 'label', label: 'Tedarikçi' },
                { key: 'value', label: 'Hacim', align: 'right', render: (v) => formatTRY(v) },
              ]}
              rows={cockpit.top_suppliers}
              onRowClick={(r) => drill(`/analiz/tedarikci/${r.id}`)}
            />
          )}
        </ChartCard>

        <ChartCard title="Kritik Uyarılar" subtitle="Tüm sistem · anlık durum (firma/dönem filtresinden bağımsız)" loading={loading} error={error}
                   empty={!loading && (!cockpit?.critical_alerts || cockpit.critical_alerts.every(a => a.count === 0))}>
          {cockpit?.critical_alerts && (
            <DataTable
              columns={[
                { key: 'label', label: 'Konu' },
                { key: 'count', label: 'Sayı', align: 'right' },
              ]}
              rows={cockpit.critical_alerts.filter(a => a.count > 0)}
              emptyLabel="Her şey yolunda"
              onRowClick={(r) => r.link && drill(r.link)}
            />
          )}
        </ChartCard>
      </div>

      {/* What-if simülasyonu — açılır panel (default kapalı) */}
      <WhatifPanel range={range} firma={firma} />

      <footer style={{ textAlign: 'center', color: theme.color.muted, fontSize: theme.font.size.xs, padding: theme.space.lg }}>
        Veri kaynakları: POS, Fatura, GRN, Stok, Komisyon Oranları · Bu sayfayı PDF olarak dışa aktarmak için sağ üstteki dışa aktar butonunu kullanın.
      </footer>
    </div>
  );
}
